import React, { useState } from 'react';
import { ExternalLink, Info, X } from 'lucide-react';

/**
 * SPONSOR TILE COMPONENT — LOCKED DIRECTIVE
 * 
 * Static, vetted placement that appears in:
 * - Category pages (below header, above group listings)
 * - Commons pages (if steward has enabled it)
 * 
 * Rules:
 * - Max 1 sponsor per category/commons
 * - No infinite scroll
 * - No autoplay video
 * - No sound by default
 * - Static preview only
 * - Click-through only after user intent
 * - Clearly labeled "Sponsor"
 * - "Why am I seeing this?" transparency
 */

export default function SponsorTile({ sponsor, variant = 'category' }) {
  const [showTransparency, setShowTransparency] = useState(false);

  if (!sponsor) return null;

  const handleClick = () => {
    if (sponsor.destination_url) {
      if (sponsor.is_external_link) {
        // External links open in new tab
        window.open(sponsor.destination_url, '_blank', 'noopener,noreferrer');
      } else {
        // Internal links navigate normally
        window.location.href = sponsor.destination_url;
      }
    }
  };

  return (
    <div 
      className="relative rounded-xl overflow-hidden"
      style={{ 
        backgroundColor: 'var(--fy-surface-card)',
        border: '1px solid var(--fy-border-subtle)'
      }}
      data-testid="sponsor-tile"
    >
      {/* Sponsor Label - Always visible */}
      <div 
        className="absolute top-2 left-2 z-10 px-2 py-0.5 rounded text-xs font-medium"
        style={{ 
          backgroundColor: 'rgba(0,0,0,0.6)', 
          color: '#ffffff' 
        }}
      >
        Sponsor
      </div>

      {/* Transparency Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setShowTransparency(true);
        }}
        className="absolute top-2 right-2 z-10 p-1.5 rounded-full"
        style={{ 
          backgroundColor: 'rgba(0,0,0,0.6)', 
          color: '#ffffff' 
        }}
        aria-label="Why am I seeing this?"
        data-testid="sponsor-transparency-btn"
      >
        <Info className="w-3.5 h-3.5" />
      </button>

      {/* Clickable Content Area */}
      <button
        onClick={handleClick}
        className="w-full text-left focus:outline-none focus:ring-2 focus:ring-offset-2"
        style={{ '--tw-ring-color': '#7A8F5B' }}
        disabled={!sponsor.destination_url}
        data-testid="sponsor-content"
      >
        {/* Image (if provided) */}
        {sponsor.image_url && (
          <div className="relative w-full h-32 overflow-hidden">
            <img
              src={sponsor.image_url}
              alt={sponsor.title}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        )}

        {/* Text Content */}
        <div className="p-4">
          <h3 
            className="font-bold text-sm mb-1 line-clamp-2"
            style={{ color: 'var(--fy-text-primary)' }}
          >
            {sponsor.title}
            {sponsor.is_external_link && (
              <ExternalLink className="inline-block w-3.5 h-3.5 ml-1 opacity-60" />
            )}
          </h3>
          <p 
            className="text-xs line-clamp-3"
            style={{ color: 'var(--fy-text-secondary)' }}
          >
            {sponsor.description}
          </p>
        </div>
      </button>

      {/* Transparency Modal */}
      {showTransparency && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setShowTransparency(false)}
        >
          <div 
            className="relative w-full max-w-sm rounded-2xl p-6"
            style={{ backgroundColor: 'var(--fy-surface-card)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowTransparency(false)}
              className="absolute top-4 right-4 p-1 rounded-lg"
              style={{ color: 'var(--fy-text-secondary)' }}
            >
              <X className="w-5 h-5" />
            </button>

            <h3 
              className="text-lg font-bold mb-4"
              style={{ color: 'var(--fy-text-primary)' }}
            >
              Why am I seeing this?
            </h3>

            <p 
              className="text-sm mb-4"
              style={{ color: 'var(--fy-text-secondary)' }}
            >
              This is a sponsorship placement, not an advertisement.
            </p>

            <div 
              className="p-3 rounded-xl text-sm"
              style={{ backgroundColor: 'var(--fy-surface-muted)' }}
            >
              <p className="mb-2 font-medium" style={{ color: 'var(--fy-text-primary)' }}>
                LikeKind sponsorship means:
              </p>
              <ul className="space-y-1.5 text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
                <li>• No targeting — this placement is not based on your behavior</li>
                <li>• No analytics — sponsors receive no data about you</li>
                <li>• No tracking — your activity is not monitored</li>
                <li>• No influence — sponsors cannot affect what you see elsewhere</li>
              </ul>
              <p className="mt-3 text-xs" style={{ color: 'var(--fy-text-disabled)' }}>
                Sponsorships support LikeKind. They appear by category, not by user profile.
              </p>
            </div>

            <button
              onClick={() => setShowTransparency(false)}
              className="w-full mt-4 py-2 rounded-lg font-medium"
              style={{ 
                backgroundColor: 'var(--fy-surface-muted)',
                color: 'var(--fy-text-primary)'
              }}
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
